/**
 * The shared API client. EVERY call goes through here.
 *
 * No component calls `fetch` directly (naming conventions §9). One client means one place that
 * carries the retry policy, one place that shapes errors, and one place a header can be added.
 */

import { redirectToLoginOnSessionExpired } from './session_guard'

export interface ApiErrorDetail {
  /** What to show a person. */
  message: string
  /** What to paste into a bug report — BlueHomer §3.37 requires both. */
  technical: string
  status: number
  correlationId?: string
}

export class ApiError extends Error {
  readonly detail: ApiErrorDetail

  constructor(detail: ApiErrorDetail) {
    super(detail.message)
    this.name = 'ApiError'
    this.detail = detail
  }
}

/**
 * A 401 from any `/api` route means the session is gone — expired, cleared, or never established.
 * It is handled in ONE place so no screen has to know about authentication, and so a dead session
 * can never leave the user on a page that silently fails forever.
 */
function handleUnauthorized(status: number): void {
  if (status === 401) redirectToLoginOnSessionExpired()
}

const RETRYABLE = new Set([502, 503, 504])
const MAX_ATTEMPTS = 3
const BASE_DELAY_MS = 250

function sleep(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/** Pull a human message out of whatever shape the error arrived in. */
function humanMessage(status: number, body: any): string {
  if (typeof body?.detail === 'string') return body.detail
  if (typeof body?.detail?.message === 'string') return body.detail.message
  if (typeof body?.detail?.detail === 'string') return body.detail.detail
  if (typeof body?.message === 'string') return body.message
  if (Array.isArray(body?.detail) && body.detail[0]?.msg) {
    //  FastAPI validation errors: name the field, because "validation failed" tells nobody which.
    return body.detail
      .map((d: any) => `${(d.loc ?? []).slice(-1)[0] ?? 'field'}: ${d.msg}`)
      .join('; ')
  }
  if (status === 401) return 'Your session has ended. Please sign in again.'
  if (status === 403) return 'You do not have access to that, or it belongs to another account.'
  if (status === 404) return 'That record no longer exists.'
  if (status === 409) return 'That conflicts with an existing record.'
  if (status >= 500) return 'The service is not responding correctly.'
  return `Request failed (${status}).`
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let lastError: ApiError | null = null

  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    let response: Response
    try {
      response = await fetch(path, {
        method,
        headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
        //  The session cookie is HttpOnly, so it travels only because the browser attaches it.
        //  Same-origin is today's default; naming it means moving the API to another origin fails
        //  loudly at the fetch rather than quietly as "every request is unauthenticated".
        credentials: 'same-origin',
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch (networkError) {
      lastError = new ApiError({
        message: 'Cannot reach Tidalfin. Check that the application server is running.',
        technical: `${method} ${path} — ${String(networkError)}`,
        status: 0,
      })
      //  A network failure is worth retrying; a 400 is not.
      if (attempt < MAX_ATTEMPTS) {
        await sleep(BASE_DELAY_MS * 2 ** (attempt - 1))
        continue
      }
      throw lastError
    }

    const correlationId = response.headers.get('X-Correlation-ID') ?? undefined

    if (response.ok) {
      if (response.status === 204) return undefined as T
      const text = await response.text()
      return (text ? JSON.parse(text) : undefined) as T
    }

    let parsed: any = null
    try {
      parsed = JSON.parse(await response.text())
    } catch {
      parsed = null
    }

    //  Before building the error: a dead session must move the user, not just show them a
    //  message on a screen that will never load again.
    handleUnauthorized(response.status)

    lastError = new ApiError({
      message: humanMessage(response.status, parsed),
      technical: JSON.stringify(
        { method, path, status: response.status, correlationId, body: parsed },
        null,
        2,
      ),
      status: response.status,
      correlationId,
    })

    //  Only retry what can plausibly succeed on a second try. Retrying a 409 just produces the
    //  same conflict three times and delays telling the user.
    if (RETRYABLE.has(response.status) && attempt < MAX_ATTEMPTS) {
      await sleep(BASE_DELAY_MS * 2 ** (attempt - 1))
      continue
    }
    throw lastError
  }

  throw lastError ?? new ApiError({ message: 'Request failed', technical: path, status: 0 })
}

function query(params: Record<string, unknown>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    //  Skip null/undefined/'' so an untouched filter never reaches the service as a real value.
    if (value === undefined || value === null || value === '') continue
    search.set(key, String(value))
  }
  const qs = search.toString()
  return qs ? `?${qs}` : ''
}

/**
 * Upload one file.
 *
 * NOT routed through `request`: that sets `Content-Type: application/json`, and a fetch given a
 * FormData body with an explicit Content-Type never gets the multipart boundary appended — the
 * request goes out claiming JSON while carrying multipart, and the server reports the form fields
 * as missing. Leaving the header off entirely is what makes the browser set it correctly.
 *
 * NOT RETRIED either. The retry in `request` is safe because those calls are idempotent; re-sending
 * a file after a timeout is how one resume becomes three.
 */
export async function uploadFile<T>(
  path: string,
  file: File,
  fields: Record<string, string> = {},
): Promise<T> {
  const form = new FormData()
  form.append('file', file)
  for (const [key, value] of Object.entries(fields)) form.append(key, value)

  let response: Response
  try {
    response = await fetch(`/api${path}`, {
      method: 'POST',
      credentials: 'same-origin',
      body: form,
    })
  } catch (networkError) {
    throw new ApiError({
      message: 'The upload could not be sent. Check that the application server is running.',
      technical: `POST /api${path} — ${String(networkError)}`,
      status: 0,
    })
  }

  const body = await response.json().catch(() => null)
  if (!response.ok) {
    handleUnauthorized(response.status)
    throw new ApiError({
      message: humanMessage(response.status, body),
      technical: `POST /api${path} — ${response.status} ${JSON.stringify(body).slice(0, 800)}`,
      status: response.status,
      correlationId: response.headers.get('X-Correlation-ID') ?? undefined,
    })
  }
  return body as T
}

export const api = {
  get: <T>(path: string, params: Record<string, unknown> = {}) =>
    request<T>('GET', `/api${path}${query(params)}`),
  post: <T>(path: string, body?: unknown) => request<T>('POST', `/api${path}`, body ?? {}),
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', `/api${path}`, body),
  del: <T>(path: string) => request<T>('DELETE', `/api${path}`),
}
